import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { History, ArrowLeft, ArrowRight } from 'lucide-react';
import { updateBankTransaction } from '@/lib/bankTransactionsApi';
import { toast } from 'sonner';

const getSubcats = (categories, catName) => categories.find(c => c.name === catName)?.subcategories || [];
const getChildren = (categories, catName, subcatName) => {
  const sub = getSubcats(categories, catName).find(s => s.name === subcatName);
  return sub?.children || [];
};

const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-').map(Number);
  return `${m}/${day}/${y}`;
};

const fmtMoney = (v) => {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const fmtHistoryTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default function TransactionEditDialog({ open, onOpenChange, transaction, categories, onSaved, onPrev, onNext, hasPrev, hasNext, position }) {
  const [form, setForm] = useState({ title: '', description: '', date: '', amount: '', category: '', subcategory: '', detail: '' });
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (transaction) {
      setForm({
        title: transaction.title || '',
        description: transaction.description || '',
        date: transaction.date || '',
        amount: transaction.amount != null ? String(transaction.amount) : '',
        category: transaction.category || '',
        subcategory: transaction.subcategory || '',
        detail: transaction.detail || '',
      });
      setShowHistory(false);
      setDirty(false);
    }
  }, [transaction]);

  const patchForm = (patch) => { setForm(f => ({ ...f, ...patch })); setDirty(true); };

  const subcatOpts = useMemo(() => getSubcats(categories, form.category), [form.category, categories]);
  const detailOpts = useMemo(() => getChildren(categories, form.category, form.subcategory), [form.category, form.subcategory, categories]);

  const history = transaction?.edit_history || [];

  // Keep latest handlers in a ref so the keydown listener always uses current state
  const handlersRef = useRef({});

  // Keyboard shortcuts: ← Prev, → Next, Cmd/Ctrl+Enter Save
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      const t = e.target;
      const tag = t?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable;

      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handlersRef.current.handleSave?.();
        return;
      }

      if (isTyping) return;

      if (e.key === 'ArrowRight' && handlersRef.current.hasNext) {
        e.preventDefault();
        handlersRef.current.navigate?.('next');
      } else if (e.key === 'ArrowLeft' && handlersRef.current.hasPrev) {
        e.preventDefault();
        handlersRef.current.navigate?.('prev');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!transaction) return null;

  const doSave = async () => {
    const amt = parseFloat(form.amount);
    if (!form.date || !form.description || form.amount === '' || isNaN(amt)) {
      return { ok: false, reason: 'invalid' };
    }
    try {
      const updated = await updateBankTransaction(transaction.id, {
        title: form.title,
        description: form.description,
        date: form.date,
        amount: amt,
        category: form.category,
        subcategory: form.subcategory,
        detail: form.detail,
      });
      if (onSaved) onSaved(updated);
      setDirty(false);
      return { ok: true };
    } catch {
      return { ok: false, reason: 'error' };
    }
  };

  const handleSave = async () => {
    if (!form.date || !form.description || form.amount === '') {
      toast.error('Date, description, and amount are required');
      return;
    }
    if (isNaN(parseFloat(form.amount))) {
      toast.error('Amount must be a number');
      return;
    }
    setSaving(true);
    const result = await doSave();
    setSaving(false);
    if (result.ok) {
      toast.success('Transaction updated');
      onOpenChange(false);
    } else {
      toast.error('Failed to update transaction');
    }
  };

  const navigate = async (direction) => {
    if (dirty) {
      setSaving(true);
      const result = await doSave();
      setSaving(false);
      if (!result.ok) {
        toast.error(result.reason === 'invalid' ? 'Fix invalid fields before navigating' : 'Save failed');
        return;
      }
      toast.success('Saved');
    }
    if (direction === 'next' && onNext) onNext();
    if (direction === 'prev' && onPrev) onPrev();
  };

  // Update ref with latest handlers on each render
  handlersRef.current = { handleSave, navigate, hasPrev, hasNext };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-transaction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span>Edit Transaction</span>
            {position && (
              <span className="text-[11px] font-normal text-muted-foreground tabular-nums" data-testid="edit-position">
                {position.current} of {position.total}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <span>Edit title, description, date, amount, and category</span>
            {(onPrev || onNext) && (
              <span className="text-[10px] text-muted-foreground/80" data-testid="shortcut-hint">
                <kbd className="px-1 py-0 rounded border border-border/60 bg-[#faf8f4] text-[9px]">←</kbd>
                <kbd className="ml-0.5 px-1 py-0 rounded border border-border/60 bg-[#faf8f4] text-[9px]">→</kbd>
                <span className="ml-1">nav</span>
                <span className="mx-1">·</span>
                <kbd className="px-1 py-0 rounded border border-border/60 bg-[#faf8f4] text-[9px]">⌘/Ctrl</kbd>
                <kbd className="ml-0.5 px-1 py-0 rounded border border-border/60 bg-[#faf8f4] text-[9px]">↵</kbd>
                <span className="ml-1">save</span>
              </span>
            )}
            {dirty && <span className="text-amber-600 font-medium">• unsaved changes</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-1">
            <Label className="text-xs">Title</Label>
            <Input
              value={form.title}
              onChange={e => patchForm({ title: e.target.value })}
              placeholder="Short label (e.g., Rent from Unit 2)"
              data-testid="edit-title"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea
              value={form.description}
              onChange={e => patchForm({ description: e.target.value })}
              rows={2}
              className="text-xs"
              data-testid="edit-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={e => patchForm({ date: e.target.value })} data-testid="edit-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Amount (+ income / - expense)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => patchForm({ amount: e.target.value })} data-testid="edit-amount" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select value={form.category || '__none__'} onValueChange={v => patchForm({ category: v === '__none__' ? '' : v, subcategory: '', detail: '' })}>
                <SelectTrigger className="text-xs" data-testid="edit-category"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {subcatOpts.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Subcategory</Label>
                <Select value={form.subcategory || '__none__'} onValueChange={v => patchForm({ subcategory: v === '__none__' ? '' : v, detail: '' })}>
                  <SelectTrigger className="text-xs" data-testid="edit-subcategory"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {subcatOpts.map(s => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {detailOpts.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Detail</Label>
                <Select value={form.detail || '__none__'} onValueChange={v => patchForm({ detail: v === '__none__' ? '' : v })}>
                  <SelectTrigger className="text-xs" data-testid="edit-detail"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {detailOpts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {history.length > 0 && (
            <div className="border-t border-border/40 pt-2 mt-2">
              <button
                onClick={() => setShowHistory(s => !s)}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                data-testid="toggle-edit-history"
              >
                <History className="h-3 w-3" />
                {showHistory ? 'Hide' : 'Show'} edit history ({history.length})
              </button>
              {showHistory && (
                <ul className="mt-2 space-y-1 text-[11px] border border-border/40 rounded-md p-2 bg-[#faf8f4]" data-testid="edit-history-list">
                  {history.map((h, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-muted-foreground tabular-nums flex-shrink-0 w-36">{fmtHistoryTime(h.edited_at)}</span>
                      <span>
                        <span className="font-medium capitalize">{h.field}</span>:{' '}
                        <span className="line-through text-red-600">
                          {h.field === 'amount' ? fmtMoney(h.old_value) : fmtDate(h.old_value)}
                        </span>{' '}
                        →{' '}
                        <span className="text-emerald-700 font-medium">
                          {h.field === 'amount' ? fmtMoney(h.new_value) : fmtDate(h.new_value)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
          <div className="flex gap-1">
            {(onPrev || onNext) && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('prev')}
                  disabled={!hasPrev || saving}
                  data-testid="edit-prev-btn"
                >
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" />Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('next')}
                  disabled={!hasNext || saving}
                  data-testid="edit-next-btn"
                >
                  Next<ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="edit-save-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
